'use client';
import { useEffect, useState } from 'react';
import { seoAPI } from '@/config/api';
import { extractSeoId, shouldFetchSeo } from '@/utils/seoUtils';

/**
 * SEO Component for managing metadata dynamically from API
 * Fetches SEO data from backend based on pathname
 */

export default function SEO({ pathname = '' }) {
  const [seoData, setSeoData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSeoData = async () => {
      // Check if we should fetch SEO for this path
      if (!shouldFetchSeo(pathname)) {
        setLoading(false);
        return;
      }

      const seoId = extractSeoId(pathname);
      if (!seoId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await seoAPI.getSEOData(seoId);

        if (response?.seo_data && Array.isArray(response.seo_data) && response.seo_data.length > 0) {
          setSeoData(response);
        } else {
          // No data from database, set to null to trigger defaults
          setSeoData(null);
        }
      } catch (error) {
        console.warn('Failed to fetch SEO data:', error);
        // Set to null to trigger defaults when API fails
        setSeoData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchSeoData();
  }, [pathname]);

  // Facebook Pixel is now handled by src/components/Analytics/FacebookPixel.jsx

  useEffect(() => {
    if (loading) return;

    // Helper to set and position title correctly
    const setDocumentTitle = (newTitle) => {
      document.title = newTitle;
      const titleTags = document.head.getElementsByTagName('title');
      if (titleTags.length > 0) {
        // Keep the first title tag, remove duplicates
        const mainTitle = titleTags[0];
        for (let i = 1; i < titleTags.length; i++) {
          titleTags[i].remove();
        }
        mainTitle.textContent = newTitle;
        // Move to the very top of head
        if (document.head.firstChild !== mainTitle) {
          document.head.insertBefore(mainTitle, document.head.firstChild);
        }
      } else {
        const titleEl = document.createElement('title');
        titleEl.textContent = newTitle;
        document.head.insertBefore(titleEl, document.head.firstChild);
      }
    };

    // Default SEO values
    const defaultTitle = 'Hyrelancer';
    const defaultDescription = 'Hyrelancer connects skilled Indians with real freelance opportunities across India. Turn your talent into income, full-time or as a side hustle.';
    const defaultKeywords = 'Hyrelancer, freelance jobs India, hire freelancers India, Indian freelancers, online work India, freelancer marketplace, digital jobs India, talent platform India';

    // Apply defaults immediately as a baseline
    setDocumentTitle(defaultTitle);
    updateMetaTag('name', 'description', defaultDescription);
    updateMetaTag('name', 'keywords', defaultKeywords);

    // If valid SEO data exists, it will overwrite the defaults
    if (seoData && seoData?.seo_data && Array.isArray(seoData.seo_data) && seoData.seo_data.length > 0) {
      seoData.seo_data.forEach(item => {
        if (!item.seot_type || !item.seop_data || String(item.seop_data).trim() === '') return;

        switch (item.seot_type) {
          case 'title':
            setDocumentTitle(item.seop_data);
            break;
          case 'description':
            updateMetaTag('name', 'description', item.seop_data);
            break;
          case 'key':
            updateMetaTag('name', 'keywords', item.seop_data);
            break;
          case 'common':
            parseCommonMetaTags(item.seop_data);
            break;
          case 'property':
          case 'name':
          case 'canonical':
            break;
          default:
            break;
        }
      });
    }

    // Generate canonical URL from current pathname (not from database)
    const siteBaseUrl = typeof window !== 'undefined' ? window.location.origin : '';
    const canonicalUrl = `${siteBaseUrl}${pathname}`;
    updateCanonicalLink(canonicalUrl);

  }, [seoData, loading, pathname]);

  return null; // This component doesn't render anything
}

/**
 * Get the insertion point for meta tags (after charset and viewport)
 * Returns the element to insert after, or null to append at top
 */
function getMetaTagInsertionPoint() {
  // Try to find viewport meta tag first
  const viewport = document.head.querySelector('meta[name="viewport"]');
  if (viewport && viewport.nextSibling) {
    return viewport.nextSibling;
  }

  // If no viewport, try charset
  const charset = document.head.querySelector('meta[charset]');
  if (charset && charset.nextSibling) {
    return charset.nextSibling;
  }

  // If no charset, try first meta tag
  const firstMeta = document.head.querySelector('meta');
  if (firstMeta && firstMeta.nextSibling) {
    return firstMeta.nextSibling;
  }

  // If no meta tags, use first child of head
  return document.head.firstChild;
}

/**
 * Update or create a meta tag - inserts at top of head
 */
function updateMetaTag(attribute, name, content) {
  if (!content) return;

  let metaTag = document.querySelector(`meta[${attribute}="${name}"]`);

  if (!metaTag) {
    metaTag = document.createElement('meta');
    metaTag.setAttribute(attribute, name);

    // Insert at top of head (after charset/viewport)
    const insertPoint = getMetaTagInsertionPoint();
    if (insertPoint) {
      document.head.insertBefore(metaTag, insertPoint);
    } else {
      // Fallback: insert after first child or at beginning
      const firstChild = document.head.firstChild;
      if (firstChild) {
        document.head.insertBefore(metaTag, firstChild);
      } else {
        document.head.appendChild(metaTag);
      }
    }
  }

  metaTag.setAttribute('content', content);
}

/**
 * Update canonical link - place it at top of head (after meta tags)
 */
function updateCanonicalLink(href) {
  // Remove existing canonical link if it exists
  const existingCanonical = document.querySelector('link[rel="canonical"]');
  if (existingCanonical) {
    existingCanonical.remove();
  }

  // Create new canonical link
  const canonicalLink = document.createElement('link');
  canonicalLink.setAttribute('rel', 'canonical');
  canonicalLink.setAttribute('href', href);

  // Find the last meta tag to insert canonical after it
  const allMetaTags = document.head.querySelectorAll('meta');
  if (allMetaTags.length > 0) {
    const lastMeta = allMetaTags[allMetaTags.length - 1];
    // Insert after the last meta tag
    if (lastMeta.nextSibling) {
      document.head.insertBefore(canonicalLink, lastMeta.nextSibling);
    } else {
      // If last meta has no next sibling, append after it
      document.head.appendChild(canonicalLink);
    }
  } else {
    // If no meta tags, insert at top using insertion point
    const insertPoint = getMetaTagInsertionPoint();
    if (insertPoint) {
      document.head.insertBefore(canonicalLink, insertPoint);
    } else {
      // Fallback: insert at beginning
      const firstChild = document.head.firstChild;
      if (firstChild) {
        document.head.insertBefore(canonicalLink, firstChild);
      } else {
        document.head.appendChild(canonicalLink);
      }
    }
  }
}

/**
 * Parse common field meta tags from HTML format
 * Example: <meta property="og:site_name" content="Site Name"/>
 */
function parseCommonMetaTags(htmlContent) {
  if (!htmlContent) return;

  try {
    // Decode HTML entities first (e.g., &amp; -> &)
    const tempTextarea = document.createElement('textarea');
    tempTextarea.innerHTML = htmlContent.trim();
    const decodedHtml = tempTextarea.value;

    // Create a temporary DOM element to parse HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = decodedHtml;

    // 1. Handle Meta Tags
    const metaTags = tempDiv.querySelectorAll('meta');
    metaTags.forEach(meta => {
      const property = meta.getAttribute('property');
      const name = meta.getAttribute('name');
      const content = meta.getAttribute('content');

      if (content) {
        // Decode content as well in case it has HTML entities
        const contentTextarea = document.createElement('textarea');
        contentTextarea.innerHTML = content;
        const decodedContent = contentTextarea.value;

        if (property) {
          updateMetaTag('property', property, decodedContent);
        } else if (name) {
          updateMetaTag('name', name, decodedContent);
        }
      }
    });

    // 2. Handle Script Tags (e.g., Google Tag Manager)
    const scriptTags = tempDiv.querySelectorAll('script');
    scriptTags.forEach(script => {
      // Check if this script is already in the head to avoid duplicates
      let exists = false;
      const existingScripts = document.head.getElementsByTagName('script');

      if (script.src) {
        for (let i = 0; i < existingScripts.length; i++) {
          if (existingScripts[i].src === script.src) {
            exists = true;
            break;
          }
        }
      } else if (script.innerHTML) {
        // Check for inline script content match
        for (let i = 0; i < existingScripts.length; i++) {
          // Normalize whitespace for comparison if possible, but exact match is safer for code
          if (existingScripts[i].innerHTML === script.innerHTML) {
            exists = true;
            break;
          }
        }
      }

      if (!exists) {
        const newScript = document.createElement('script');
        // Copy all attributes
        Array.from(script.attributes).forEach(attr => {
          newScript.setAttribute(attr.name, attr.value);
        });

        // Copy content
        if (script.innerHTML) {
          newScript.innerHTML = script.innerHTML;
        }

        document.head.appendChild(newScript);
      }
    });

    // 3. Handle NoScript Tags
    const noscriptTags = tempDiv.querySelectorAll('noscript');
    noscriptTags.forEach(noscript => {
      // Check for existence in body
      const existingNoscripts = document.body.getElementsByTagName('noscript');
      let exists = false;

      // Compare innerHTML (which usually contains the iframe for GTM)
      for (let i = 0; i < existingNoscripts.length; i++) {
        if (existingNoscripts[i].innerHTML === noscript.innerHTML) {
          exists = true;
          break;
        }
      }

      if (!exists) {
        const newNoscript = document.createElement('noscript');
        Array.from(noscript.attributes).forEach(attr => newNoscript.setAttribute(attr.name, attr.value));
        newNoscript.innerHTML = noscript.innerHTML;

        // Insert at the beginning of body as recommended for GTM
        if (document.body.firstChild) {
          document.body.insertBefore(newNoscript, document.body.firstChild);
        } else {
          document.body.appendChild(newNoscript);
        }
      }
    });

  } catch (error) {
    console.warn('Error parsing common meta tags:', error);
  }
}
